import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Building2, LayoutGrid, List, Users } from 'lucide-react';
import { format } from 'date-fns';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { useSelector } from 'react-redux';
import { selectCompanyName } from '@/store/slices/companySlice';
import { selectActiveCompanyId } from '@/store/slices/workspaceSlice';
import { useEmployees, useDepartments } from '@/api/queries/employee.queries';
import { useDailyAttendance } from '@/api/queries/attendance.queries';
import { StatCard } from '@/components/shared/StatCard';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { ReportExportBar } from '@/components/reports/ReportExportBar';
import { buildStrengthRows } from '@/lib/ReportGenerator';
import { cn } from '@/lib/cn';

const PIE_COLORS = ['#22c55e', '#ef4444', '#3b82f6', '#f59e0b'];

function DeptCard({ dept, employees, attendance }) {
  const empInDept = useMemo(() =>
    employees.filter(e => (e.department?.name || e.department) === dept.name || (e.department?.id || e.department_id) === dept.id),
    [employees, dept]);

  const attByEmp = useMemo(() => {
    const m = {};
    attendance.forEach(a => { m[a.employee_id] = a; });
    return m;
  }, [attendance]);

  const stats = useMemo(() => {
    let present = 0, absent = 0, leave = 0, late = 0;
    empInDept.forEach(e => {
      const a = attByEmp[e.id];
      if (!a || a.status === 'ABSENT') absent++;
      else if (a.status === 'ON_LEAVE') leave++;
      else if (a.status === 'LATE') { late++; present++; }
      else present++;
    });
    return { present, absent, leave, late, total: empInDept.length };
  }, [empInDept, attByEmp]);

  const pct = stats.total ? Math.round((stats.present / stats.total) * 100) : 0;
  const pieData = [
    { name: 'Present', value: stats.present },
    { name: 'Absent',  value: stats.absent  },
    { name: 'Leave',   value: stats.leave   },
    { name: 'Late',    value: stats.late    },
  ].filter(d => d.value > 0);

  return (
    <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-5">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-semibold text-[var(--text-primary)]">{dept.name}</h3>
          <p className="text-[12px] text-[var(--text-tertiary)] mt-0.5">{stats.total} employees</p>
        </div>
        <div className="text-right">
          <p className={cn('text-2xl font-bold', pct >= 90 ? 'text-green-600' : pct >= 75 ? 'text-amber-500' : 'text-red-500')}>
            {pct}%
          </p>
          <p className="text-[11px] text-[var(--text-tertiary)]">attendance</p>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="w-20 h-20 shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" innerRadius={20} outerRadius={38} dataKey="value" startAngle={90} endAngle={-270}>
                {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]}/>)}
              </Pie>
              <Tooltip formatter={(v, n) => [v, n]} contentStyle={{ fontSize: 11 }}/>
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="flex-1 grid grid-cols-2 gap-2">
          {[
            { label: 'Present', val: stats.present, cls: 'text-green-600' },
            { label: 'Absent',  val: stats.absent,  cls: 'text-red-500'   },
            { label: 'Leave',   val: stats.leave,   cls: 'text-blue-600'  },
            { label: 'Late',    val: stats.late,    cls: 'text-amber-600' },
          ].map(s => (
            <div key={s.label} className="flex items-center gap-1.5">
              <span className={cn('text-lg font-bold', s.cls)}>{s.val}</span>
              <span className="text-[11px] text-[var(--text-tertiary)]">{s.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function StrengthReportPage() {
  const companyName     = useSelector(selectCompanyName);
  const activeCompanyId = useSelector(selectActiveCompanyId);
  const today = format(new Date(), 'yyyy-MM-dd');
  const [date, setDate]       = useState(today);
  const [deptFilter, setDept] = useState('');
  const [view, setView]       = useState('cards'); // 'cards' | 'table'

  const { data: empData }  = useEmployees({ status: 'ACTIVE', limit: 500 });
  const { data: deptData } = useDepartments();
  const { data: attData, isLoading } = useDailyAttendance({ date, limit: 500 }, activeCompanyId);

  const employees   = empData?.data   || [];
  const departments = deptData?.data  || [];
  const attendance  = attData?.data   || [];

  const attByEmp = useMemo(() => {
    const m = {};
    attendance.forEach(a => { m[a.employee_id] = a; });
    return m;
  }, [attendance]);

  const filteredDepts = deptFilter
    ? departments.filter(d => d.id === deptFilter)
    : departments;

  // Grand totals
  const totals = useMemo(() => {
    let present = 0, absent = 0, leave = 0, late = 0;
    employees.forEach(e => {
      const a = attByEmp[e.id];
      if (!a || a.status === 'ABSENT') absent++;
      else if (a.status === 'ON_LEAVE') leave++;
      else if (a.status === 'LATE') { late++; present++; }
      else present++;
    });
    return { present, absent, leave, late, total: employees.length };
  }, [employees, attByEmp]);

  const exportRows = useMemo(() => buildStrengthRows(
    departments.map(d => {
      const empsInDept = employees.filter(e => (e.department?.id || e.department_id) === d.id);
      let present = 0, absent = 0, leave = 0, late = 0;
      empsInDept.forEach(e => {
        const a = attByEmp[e.id];
        if (!a || a.status === 'ABSENT') absent++;
        else if (a.status === 'ON_LEAVE') leave++;
        else if (a.status === 'LATE') { late++; present++; }
        else present++;
      });
      return { department: d.name, total: empsInDept.length, present, absent, leave, late };
    })
  ), [departments, employees, attByEmp]);

  return (
    <div className="p-6 space-y-5">
      <nav className="flex items-center gap-1.5 text-[12px] text-[var(--text-tertiary)]">
        <Link to="/reports" className="hover:text-[var(--accent)]">Reports</Link>
        <ChevronRight size={12}/>
        <span className="text-[var(--text-primary)] font-medium">Strength</span>
      </nav>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-[var(--text-primary)]">Strength Report</h1>
          <p className="text-[13px] text-[var(--text-secondary)] mt-0.5">{date} · {companyName}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-[var(--border)] overflow-hidden">
            <button onClick={() => setView('cards')} className={cn('h-8 px-3 text-[12px] flex items-center gap-1.5', view === 'cards' ? 'bg-[var(--accent)] text-white' : 'text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]')}>
              <LayoutGrid size={13}/> Cards
            </button>
            <button onClick={() => setView('table')} className={cn('h-8 px-3 text-[12px] flex items-center gap-1.5 border-l border-[var(--border)]', view === 'table' ? 'bg-[var(--accent)] text-white' : 'text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]')}>
              <List size={13}/> Table
            </button>
          </div>
          <ReportExportBar rows={exportRows} filename={`strength-${date}`} title="Strength Report" companyName={companyName} filterSummary={date}/>
        </div>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Total Strength" value={totals.total.toString()}   icon={Users}    color="blue"/>
        <StatCard title="Present"        value={totals.present.toString()} icon={Users}    color="green"/>
        <StatCard title="Absent"         value={totals.absent.toString()}  icon={Users}    color="red"/>
        <StatCard title="On Leave"       value={totals.leave.toString()}   icon={Building2} color="yellow"/>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <input type="date" value={date} onChange={e => setDate(e.target.value)}
          className="h-9 px-3 text-[13px] bg-[var(--bg-card)] border border-[var(--border)] rounded-lg outline-none focus:border-[var(--accent)]"/>
        <select value={deptFilter} onChange={e => setDept(e.target.value)}
          className="h-9 px-3 text-[13px] bg-[var(--bg-card)] border border-[var(--border)] rounded-lg outline-none focus:border-[var(--accent)]">
          <option value="">All Departments</option>
          {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
      </div>

      {/* Cards view */}
      {view === 'cards' && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {isLoading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-5 animate-pulse">
                <div className="h-5 bg-[var(--bg-hover)] rounded w-32 mb-3"/>
                <div className="h-20 bg-[var(--bg-hover)] rounded"/>
              </div>
            ))
          ) : filteredDepts.map(dept => (
            <DeptCard key={dept.id} dept={dept} employees={employees} attendance={attendance}/>
          ))}
        </div>
      )}

      {/* Table view */}
      {view === 'table' && (
        <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-[var(--border)] bg-[var(--bg-app)]">
                  {['Department', 'Total', 'Present', 'Absent', 'Leave', 'Late', 'Attendance %'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-[11px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredDepts.map(dept => {
                  const empsInDept = employees.filter(e => (e.department?.id || e.department_id) === dept.id);
                  let present = 0, absent = 0, leave = 0, late = 0;
                  empsInDept.forEach(e => {
                    const a = attByEmp[e.id];
                    if (!a || a.status === 'ABSENT') absent++;
                    else if (a.status === 'ON_LEAVE') leave++;
                    else if (a.status === 'LATE') { late++; present++; }
                    else present++;
                  });
                  const total = empsInDept.length;
                  const pct = total ? Math.round((present / total) * 100) : 0;
                  return (
                    <tr key={dept.id} className="border-b border-[var(--border)] last:border-0 hover:bg-[var(--bg-hover)]">
                      <td className="px-4 py-3 font-medium text-[var(--text-primary)]">{dept.name}</td>
                      <td className="px-4 py-3 text-[var(--text-secondary)]">{total}</td>
                      <td className="px-4 py-3 text-green-600 font-medium">{present}</td>
                      <td className="px-4 py-3 text-red-500 font-medium">{absent}</td>
                      <td className="px-4 py-3 text-blue-600 font-medium">{leave}</td>
                      <td className="px-4 py-3 text-amber-600 font-medium">{late}</td>
                      <td className="px-4 py-3">
                        <span className={cn('font-bold', pct >= 90 ? 'text-green-600' : pct >= 75 ? 'text-amber-500' : 'text-red-500')}>
                          {pct}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {/* Totals row */}
                <tr className="border-t-2 border-[var(--border)] bg-[var(--bg-app)] font-bold">
                  <td className="px-4 py-3">Grand Total</td>
                  <td className="px-4 py-3">{totals.total}</td>
                  <td className="px-4 py-3 text-green-600">{totals.present}</td>
                  <td className="px-4 py-3 text-red-500">{totals.absent}</td>
                  <td className="px-4 py-3 text-blue-600">{totals.leave}</td>
                  <td className="px-4 py-3 text-amber-600">{totals.late}</td>
                  <td className="px-4 py-3">{totals.total ? Math.round((totals.present / totals.total) * 100) : 0}%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
